/**
 * Smart Auto-Play Service
 * Netflix/Prime-style auto-play with Gemini AI recommendations
 */

import type { Movie, TvShow } from '../types';
import { generateAIText, isAIConfigured } from './aiProvider';

// Cache for autoplay recommendations (avoid re-calling for same movie)
const autoplayCache = new Map<number, { title: string; year: number; reason: string }>();
const AUTOPLAY_CACHE_DURATION = 60 * 60 * 1000; // 1 hour
const autoplayCacheTimestamps = new Map<number, number>();

// Seasonal contexts for recommendations
export const SEASONAL_CONTEXTS = {
  christmas: {
    months: [11, 12], // Nov-Dec
    keywords: ['christmas', 'holiday', 'winter', 'family'],
    genres: [10751, 35, 18], // Family, Comedy, Drama
  },
  halloween: {
    months: [10], // October
    keywords: ['horror', 'scary', 'thriller', 'supernatural'],
    genres: [27, 53, 9648], // Horror, Thriller, Mystery
  },
  valentines: {
    months: [2], // February
    keywords: ['romance', 'love', 'romantic comedy', 'date night'],
    genres: [10749, 35], // Romance, Comedy
  },
  summer: {
    months: [6, 7, 8], // June-Aug
    keywords: ['action', 'adventure', 'blockbuster', 'fun'],
    genres: [28, 12, 878], // Action, Adventure, Sci-Fi
  },
  fall: {
    months: [9, 10], // Sep-Oct
    keywords: ['drama', 'mystery', 'thriller'],
    genres: [18, 9648, 53], // Drama, Mystery, Thriller
  },
  spring: {
    months: [3, 4, 5], // Mar-May
    keywords: ['comedy', 'feel-good', 'lighthearted'],
    genres: [35, 10751, 10749], // Comedy, Family, Romance
  },
  newYear: {
    months: [1], // January
    keywords: ['new beginnings', 'inspiring', 'motivational'],
    genres: [18, 99, 36], // Drama, Documentary, History
  },
};

export interface SeasonalContext {
  season: keyof typeof SEASONAL_CONTEXTS | null;
  keywords: string[];
  genres: number[];
  holiday?: string;
}

export interface AutoPlayRecommendation {
  type: 'sequel' | 'prequel' | 'collection' | 'similar' | 'seasonal' | 'ai';
  media: Movie | TvShow;
  reason: string;
  confidence: number; // 0-1
}

/**
 * Get current seasonal context based on date
 */
export function getSeasonalContext(): SeasonalContext {
  const now = new Date();
  const month = now.getMonth() + 1; // 1-12
  const day = now.getDate();

  // Check for specific holidays
  let holiday: string | undefined;
  if (month === 12 && day >= 20 && day <= 31) holiday = 'Christmas';
  if (month === 1 && day <= 3) holiday = 'New Year';
  if (month === 2 && day >= 10 && day <= 14) holiday = "Valentine's Day";
  if (month === 10 && day >= 25 && day <= 31) holiday = 'Halloween';
  if (month === 11 && day >= 20 && day <= 30) holiday = 'Thanksgiving';
  if (month === 7 && day >= 1 && day <= 7) holiday = 'Independence Day';

  // Find matching season
  for (const [seasonName, config] of Object.entries(SEASONAL_CONTEXTS)) {
    if (config.months.includes(month)) {
      return {
        season: seasonName as keyof typeof SEASONAL_CONTEXTS,
        keywords: config.keywords,
        genres: config.genres,
        holiday,
      };
    }
  }

  return { season: null, keywords: [], genres: [], holiday };
}

/**
 * Build Gemini prompt for movie recommendation
 */
function buildRecommendationPrompt(
  currentMovie: Movie,
  watchHistory: string[],
  seasonalContext: SeasonalContext,
  userPrefs?: { favorites?: string[]; dislikes?: string[] }
): string {
  const seasonText = seasonalContext.holiday
    ? `It's ${seasonalContext.holiday}! Consider holiday-themed movies.`
    : seasonalContext.season
    ? `It's ${seasonalContext.season}. Consider ${seasonalContext.keywords.join(', ')} themes.`
    : '';

  const historyText = watchHistory.length > 0
    ? `Recently watched: ${watchHistory.slice(-5).join(', ')}. Avoid these.`
    : '';

  const prefText = userPrefs?.favorites?.length
    ? `User loves: ${userPrefs.favorites.slice(0, 5).join(', ')}.`
    : '';

  return `The user just finished watching "${currentMovie.title}" (${currentMovie.release_date?.split('-')[0] || 'N/A'}).
Genres: ${currentMovie.genres?.map(g => g.name).join(', ') || 'Unknown'}.

${seasonText}
${historyText}
${prefText}

Recommend ONE movie they should watch next. Consider:
1. Similar mood/vibe to what they just watched
2. Seasonal relevance if applicable
3. Movies they haven't seen recently

Respond with JSON: {"title": "Movie Title", "year": 2020, "reason": "One sentence why this is perfect next"}

JSON:`;
}

/**
 * Ask Gemini for next movie recommendation
 */
export async function askGeminiForNextMovie(
  currentMovie: Movie,
  watchHistory: string[] = [],
  userPrefs?: { favorites?: string[]; dislikes?: string[] }
): Promise<{ title: string; year: number; reason: string } | null> {
  if (!isAIConfigured()) {
    console.warn('No AI provider key for auto-play recommendations');
    return null;
  }

  // Check cache first
  const cached = autoplayCache.get(currentMovie.id);
  const cachedTime = autoplayCacheTimestamps.get(currentMovie.id);
  if (cached && cachedTime && Date.now() - cachedTime < AUTOPLAY_CACHE_DURATION) {
    console.log('🔄 AutoPlay: Cache hit (saved API call)');
    return cached;
  }

  const seasonalContext = getSeasonalContext();
  const prompt = buildRecommendationPrompt(currentMovie, watchHistory, seasonalContext, userPrefs);

  try {
    const text = await generateAIText(prompt, {
      temperature: 0.8,
      maxTokens: 256,
    });

    // Parse JSON from response
    const jsonMatch = text.match(/\{[\s\S]*\}/);
    if (jsonMatch) {
      const result = JSON.parse(jsonMatch[0]);
      // Cache the result
      autoplayCache.set(currentMovie.id, result);
      autoplayCacheTimestamps.set(currentMovie.id, Date.now());
      console.log('✅ AutoPlay: Cached recommendation for', currentMovie.title);
      return result;
    }
  } catch (error) {
    console.error('Gemini recommendation error:', error);
  }

  return null;
}

/**
 * Auto-play settings stored in localStorage
 */
export interface AutoPlaySettings {
  enabled: boolean;
  countdownSeconds: number; // Default 10
  skipIntroEnabled: boolean;
  bingeMode: boolean; // Skip countdown for TV
}

const DEFAULT_SETTINGS: AutoPlaySettings = {
  enabled: true,
  countdownSeconds: 10,
  skipIntroEnabled: true,
  bingeMode: false,
};

export function getAutoPlaySettings(): AutoPlaySettings {
  try {
    const saved = localStorage.getItem('cinevault_autoplay_settings');
    return saved ? { ...DEFAULT_SETTINGS, ...JSON.parse(saved) } : DEFAULT_SETTINGS;
  } catch {
    return DEFAULT_SETTINGS;
  }
}

export function saveAutoPlaySettings(settings: Partial<AutoPlaySettings>): void {
  const current = getAutoPlaySettings();
  localStorage.setItem('cinevault_autoplay_settings', JSON.stringify({ ...current, ...settings }));
}

/**
 * Intro skip times per show (stored in localStorage)
 * Default estimates by show type
 */
export interface IntroTimes {
  showId: number;
  startSeconds: number;
  endSeconds: number;
}

const DEFAULT_INTRO_DURATION = 45; // seconds

export function getIntroTimes(showId: number): IntroTimes | null {
  try {
    const saved = localStorage.getItem(`cinevault_intro_${showId}`);
    return saved ? JSON.parse(saved) : null;
  } catch {
    return null;
  }
}

export function saveIntroTimes(showId: number, startSeconds: number, endSeconds: number): void {
  localStorage.setItem(`cinevault_intro_${showId}`, JSON.stringify({
    showId,
    startSeconds,
    endSeconds,
  }));
}

/**
 * Estimate intro duration by show type
 */
export function estimateIntroDuration(_showName: string, genres?: number[]): number {
  const isAnime = genres?.includes(16); // Animation genre
  const isDrama = genres?.includes(18);

  // Anime often has longer openings (90s)
  if (isAnime) return 90;

  // HBO-style dramas (60s)
  if (isDrama) return 60;

  // Standard (45s)
  return DEFAULT_INTRO_DURATION;
}

/**
 * Watch history for avoiding repeat recommendations
 */
export function getWatchHistory(): string[] {
  try {
    const saved = localStorage.getItem('cinevault_watch_history');
    return saved ? JSON.parse(saved) : [];
  } catch {
    return [];
  }
}

export function addToWatchHistory(title: string): void {
  const history = getWatchHistory();
  // Keep last 50, avoid duplicates
  const filtered = history.filter(t => t !== title);
  filtered.push(title);
  if (filtered.length > 50) filtered.shift();
  localStorage.setItem('cinevault_watch_history', JSON.stringify(filtered));
}
